import { FC, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Dialog, IconButton, Stack, Typography, useMediaQuery } from '@mui/material';
import { Close } from '@mui/icons-material';
import isEmpty from 'lodash/isEmpty';

import { useDictionary } from '@cvt/hooks/useDictionary';

import { CreateTributeCardWizard } from '@modules/Tributes/partials/CreateTributeCardWizard/CreateTributeCardWizard';
import { useRouter } from '@cvt/routing';
import { TributeCardWizardProvider } from '@modules/Tributes/contexts/TributeCardWizardContext/TributeCardWizardContext';
import { cacheKeys } from '@modules/Tributes/config';


interface Props {
  open: boolean,
  onClose: () => void,
  options?: {
    tribute?: Tributes.Tribute;
  };
}

export const CreateTributeCardDialog: FC<Props> = (props) => {
  const { open, onClose, options } = props;
  const { tribute } = options || {};
  const router = useRouter();
  const dictionary = useDictionary();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const handleComplete = useCallback(() => {
    if (tribute?.id) {
      queryClient.invalidateQueries({
        queryKey: [cacheKeys.getTribute, tribute.id],
      });
      router.tributes.view(tribute.id).go();
    }
  }, [queryClient, router, tribute?.id]);
  if (isEmpty(tribute)) {
    onClose();
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <Box width="100%" px={4} py={4}>
        <Stack alignItems="center" justifyContent="space-between" direction="row" mb={2}>
          <Typography variant="h6" fontSize={18} fontWeight={600}>{dictionary.tributes.edit.card.cardDialog.title}</Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            size="small"
            sx={{
              color: 'text.secondary',
            }}
          >
            <Close/>
          </IconButton>
        </Stack>

        <Box>
          <TributeCardWizardProvider tribute={tribute}>
            <CreateTributeCardWizard  onComplete={handleComplete}/>
          </TributeCardWizardProvider>
        </Box>
      </Box>
    </Dialog>
  );
};
